import type { DefaultOptionType } from 'rc-select/lib/Select';
import React from 'react';
import { Link } from 'react-router-dom';
import {
  TbClipboard,
  TbMail,
  TbMessage,
  TbPhone,
} from 'react-icons/tb';

import Boolean from '~/components/Columns/Boolean';

import { prefetchOptions, toLabel } from '~/components/Columns/helpers';
import PhoneNumber from '~/components/Columns/PhoneNumber';


import { type CollectionColumnDefinition, type ColumnDefinition, ModelColumns } from '~/types/components';
import type {
  Call,
  Contact,
  Campaign,
  CampaignStatus,
  CampaignType,
  DefaultCallDelay,
  EmailCampaignLink,
  Group,
  SmsCampaignKeyword,
  SourceEmail,
  SourcePhoneNumber,
  User,
} from '~/types/models';
import { campaignStatusLabel, campaignTypeLabel, defaultcallDelayLabel, pointageResultLabel } from '~/types/models';

import { labelsToOptions, stripHtml, tc } from '~/helpers';

const campaignCallsColumn: CollectionColumnDefinition<Campaign, Call> = {
  title: 'Appels',
  key: 'calls',
  only: [],
  sortable: false,
  type: 'collection',
  render: (value) => {
    if (!value) {
      return <em>Chargement</em>;
    }
    if (value.length === 0) {
      return <em>Aucun appel</em>;
    }
    return (
      <ul className='CollectionColumn' style={{ margin: 0 }}>
        {value.map((c) => (
          <li key={c.id}>
            {c.contact?.firstName} {c.contact?.lastName} ({c.user?.name})
          </li>
        ))}
      </ul>
    );
  },
  with: 'calls,calls.contact,calls.user,calls.tags',
};

const campaignContactsColumn: CollectionColumnDefinition<Campaign, Contact> = {
  title: 'Contacts',
  key: 'contacts',
  only: [],
  sortable: false,
  type: 'collection',
  render(value) {
    if (!value) {
      return <em>Chargement</em>;
    }

    if (value.length === 0) {
      return <em>Aucun contact</em>;
    }

    if (value.length > 4) {
      return (
        <>
          <strong>{value.length} contacts</strong>
          <ul className='CollectionColumn' style={{ margin: 0 }}>
            {value.map((c) => (
              <li key={c.id}>
                {c.firstName} {c.lastName}
              </li>
            ))}
          </ul>
        </>
      );
    }

    return (
      <ul className='CollectionColumn' style={{ margin: 0 }}>
        {value.map((c) => (
          <li key={c.id}>
            {c.firstName} {c.lastName}
          </li>
        ))}
      </ul>
    );
  },
};

const campaignCallsCountColumn: ColumnDefinition<Campaign> = {
  title: "Nb d'appels",
  key: 'callsCount',
  sortable: false,
  type: 'count',
  render(count) {
    if (count === undefined) {
      return '';
    }

    return tc('Aucun appel|{count} appel|{count} appels', count);
  },
};

const campaignContactsCountColumn: ColumnDefinition<Campaign> = {
  title: 'Nb de contacts',
  key: 'contactsCount',
  sortable: false,
  type: 'count',
  render(count, record) {
    const id = record?.id;
    if (!id) {
      return 'Chargement...';
    }

    return (
      <Link to={`/contacts?page=1&campaigns[0]=${id}`}>
        {tc('{count} contact|{count} contacts', count)}
      </Link>
    );
  },
};

const campaignEmailsCountColumn: ColumnDefinition<Campaign> = {
  title: "Nb de courriels",
  key: 'emailsCount',
  sortable: false,
  type: 'count',
  render(count) {
    if (count === undefined) {
      return '';
    }

    return tc('Aucun courriel|{count} courriel|{count} courriels', count);
  },
};

const campaignSmsCountColumn: ColumnDefinition<Campaign> = {
  title: "Nb de textos",
  key: 'smsCount',
  sortable: false,
  type: 'count',
  render(count) {
    if (count === undefined) {
      return '';
    }

    return tc('Aucun texto|{count} texto|{count} textos', count);
  },
};


const campaignGroupsColumn: CollectionColumnDefinition<Campaign, Group> = {
  title: 'Groupes',
  key: 'groups',
  mapOptions(items: Group[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  prefetchOptions: prefetchOptions<Group>('groups', [
    {
      id: '¤',
      name: 'CSN',
      callsCount: 0,
      canLeaveMessage: false,
      contactsCount: 0,
      created: '',
      description: '',
      humanId: '',
      lastUpdated: '',
      meta: {},
      script: '',
      sequence: 0,
    },
  ]),
  sortable: false,
  toLabel: toLabel('groups', 'Groupe', 'CSN'),
  type: 'collection',
  render(value) {
    if (!Array.isArray(value)) {
      return <em>Chargement...</em>;
    }

    if (value.length === 0) {
      return (
        <ul style={{ margin: 0 }}>
          <li key={0}>CSN</li>
        </ul>
      );
    }

    return (
      <ul style={{ margin: 0 }}>
        {value.map((g) => (
          <li key={g.id}>{g.name}</li>
        ))}
      </ul>
    );
  },
};

const campaignKeywordsColumn: CollectionColumnDefinition<Campaign, SmsCampaignKeyword> = {
  title: 'Mots-clés',
  key: 'keywords',
  sortable: false,
  filterable: false,
  type: 'collection',
  render: (value) => {
    if (!value) {
      return <em>Chargement</em>;
    }

    if (value.length === 0) {
      return <em>Aucun mot-clé</em>;
    }

    return (
      <ul className='CollectionColumn' style={{ margin: 0 }}>
        {value.map((c) => (
          <li key={c.id}>
            {c.tag?.name}
            {(c.result && pointageResultLabel[c.result ?? '']) ?? ''}
          </li>
        ))}
      </ul>
    );
  },
  with: 'keywords,keywords.tag',
};

const campaignLinksColumn: CollectionColumnDefinition<Campaign, EmailCampaignLink> = {
  title: 'Liens',
  key: 'links',
  sortable: false,
  filterable: false,
  type: 'collection',
  render: (value) => {
    if (!value) {
      return <em>Chargement</em>;
    }

    if (value.length === 0) {
      return <em>Aucun lien</em>;
    }

    return (
      <ul className='CollectionColumn' style={{ margin: 0 }}>
        {value.map((c) => (
          <li key={c.id}>
            {c.tag?.name}
            {(c.result && pointageResultLabel[c.result ?? '']) ?? ''}
          </li>
        ))}
      </ul>
    );
  },
  with: 'links,links.tag',
};

const campaignUsersColumn: CollectionColumnDefinition<Campaign, User> = {
  title: 'Participant·es',
  mapOptions(items: User[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  prefetchOptions: prefetchOptions<User>('users'),
  key: 'callers',
  only: ['single'],
  sortable: false,
  type: 'collection',
  render(value) {
    if (!value) {
      return <em>Chargement</em>;
    }

    if (value.length === 0) {
      return <em>Aucun appelant</em>;
    }

    return (
      <ul className='CollectionColumn' style={{ margin: 0 }}>
        {value.map((u) => (
          <li key={u.id}>{u.name}</li>
        ))}
      </ul>
    );
  },
};

export const Columns: Array<ColumnDefinition<Campaign>> = [
  {
    title: 'Nom',
    key: 'name',
    sortable: true,
    type: 'string',
    render(name, record) {
      const humanId = record?.humanId;
      if (!humanId) {
        return 'Chargement...';
      }

      return <Link to={`/campaigns/${humanId}`}>{name}</Link>;
    },
  },
  {
    title: 'Description',
    key: 'description',
    sortable: false,
    type: 'string',
  },
  {
    title: 'Script',
    key: 'script',
    sortable: false,
    type: 'string',
    render: (value) => stripHtml(value),
  },
  {
    title: 'Corps du message',
    key: 'body',
    sortable: false,
    type: 'string',
    render: (value) => stripHtml(value),
  },
  {
    title: 'Contenu du message',
    key: 'content',
    sortable: false,
    type: 'string',
  },
  {
    title: 'Statut',
    key: 'status',
    sortable: true,
    type: 'enum',
    options: labelsToOptions(campaignStatusLabel),
    render(value: CampaignStatus) {
      return <span>{campaignStatusLabel[value] ?? 's. o.'}</span>;
    },
  },
  {
    title: 'Type',
    key: 'type',
    sortable: false,
    type: 'enum',
    options: labelsToOptions(campaignTypeLabel),
    render(value: CampaignType) {
      switch (value) {
        case 'Call':
          return <TbPhone className='anticon' size={28} />;
        case 'Email':
          return <TbMail className='anticon' size={28} />;
        case 'Sms':
          return <TbMessage className='anticon' size={28} />;
        default:
          return <TbClipboard className='anticon' size={28} />;
      }
    },
  },
  {
    title: 'Intervalle par défaut entre les appels',
    key: 'defaultCallDelay',
    sortable: false,
    type: 'enum',
    options: labelsToOptions(defaultcallDelayLabel),
    render(value: DefaultCallDelay, record) {
      if (!record) {
        return 'Chargement...';
      }

      if (record.type !== 'Call') {
        return '';
      }

      return <span>{defaultcallDelayLabel[value] ?? 's. o.'}</span>;
    },
  },
  {
    title: "Nombre d'essais maximal",
    key: 'maxTries',
    sortable: false,
    type: 'number',
  },
  {
    title: 'Peut laisser un message',
    key: 'canLeaveMessage',
    sortable: true,
    type: 'boolean',
    render: (value: boolean) => <Boolean value={value} />,
  },
  {
    title: 'Budget alloué',
    key: 'budget',
    sortable: true,
    type: 'number',
  },
  {
    title: 'Budget utilisé',
    key: 'usedBudget',
    sortable: true,
    type: 'number',
  },
  {
    title: 'Statistiques',
    key: 'stats',
    sortable: false,
    type: 'item',
    with: 'stats',
    render: () => '',
  },
  {
    title: 'Courriel source',
    key: 'sourceEmail',
    sortable: false,
    filterable: false,
    type: 'item',
    render: (v: SourceEmail | undefined) => <span>{v?.email ?? ''}</span>,
  },
  {
    title: 'Courriel de réponse',
    key: 'replyToEmail',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Numéro de téléphone source',
    key: 'sourcePhoneNumber',
    sortable: false,
    filterable: false,
    type: 'item',
    render: (v: SourcePhoneNumber | undefined) => v ? <PhoneNumber value={v.number} /> : <span />,
  },
  campaignCallsCountColumn,
  campaignCallsColumn,
  campaignContactsCountColumn,
  campaignContactsColumn,
  campaignEmailsCountColumn,
  campaignGroupsColumn,
  campaignKeywordsColumn,
  campaignLinksColumn,
  campaignSmsCountColumn,
  campaignUsersColumn,
  ...ModelColumns,
];
